<script setup>
import { useAuthStore } from '@/stores/authStore'
import { ref, computed, watch } from 'vue'

const authStore = useAuthStore()

// State for Profile Form
const editableName = ref('')
const isSaving = ref(false)
const saveMessage = ref('')
const saveError = ref('')

// State for Delete Zone
const isDeleteAcknowledged = ref(false)
const isDeleting = ref(false)

// Sync editableName with store user data
watch(() => authStore.user, (newUser) => {
  if (newUser && newUser.name) {
    editableName.value = newUser.name
  }
}, { immediate: true })

const isNameValid = computed(() => {
  return editableName.value && editableName.value.trim().length > 2
})

const handleSave = async () => {
  if (!isNameValid.value) return

  isSaving.value = true
  saveMessage.value = ''
  saveError.value = ''

  try {
    await authStore.updateUser(editableName.value)
    saveMessage.value = 'Profile updated successfully.'
  } catch (err) {
    saveError.value = 'Failed to update profile.'
  } finally {
    isSaving.value = false
    // Clear success message after 3 seconds
    setTimeout(() => { saveMessage.value = '' }, 3000)
  }
}

const handleDelete = async () => {
  if (!isDeleteAcknowledged.value) return

  const confirmed = window.confirm('Are you absolutely sure you want to delete your account? This action cannot be undone.')
  if (!confirmed) return

  isDeleting.value = true
  try {
    await authStore.deleteUser()
    // Store handles redirection to signin on success
  } catch (err) {
    alert('Failed to delete account. Please try again.')
  } finally {
    isDeleting.value = false
  }
}
</script>

<template>
  <div class="space-y-12">
    <div class="border-b border-b-gray-100 pb-4">
      <h1 class="text-xl text-gray-900">Account settings</h1>
      <p class="text-md font-light text-gray-500 mt-1">Manage your account profile and settings</p>
    </div>

    <div class="bg-white rounded-xl border border-gray-100 shadow-sm">
      <div class="px-6 py-5 border-b border-gray-100">
        <h2 class="text-lg text-gray-900">Profile</h2>
      </div>
      <div class="p-6 space-y-6">
        <div>
          <label class="block text-sm font-bold text-gray-900 mb-1">Email</label>
          <p class="text-xs text-gray-500 mb-2">The email address below is used for authentication flows. If you need to
            change it, please send an email to support@hasmcp.com.</p>
          <div class="relative">
            <input type="email" :value="authStore.user?.email" disabled
              class="w-full bg-white border border-gray-200 text-gray-400 text-sm rounded-md block p-2.5 tracking-wide cursor-not-allowed">
            <div class="absolute inset-y-0 right-0 flex items-center pr-3">
              <span
                class="bg-red-500 w-6 h-4 rounded text-[8px] flex items-center justify-center text-white font-bold cursor-pointer">•••</span>
            </div>
          </div>
        </div>

        <div>
          <label class="block text-sm font-bold text-gray-900 mb-1">Preferred name</label>
          <p class="text-xs text-gray-500 mb-2">The name specified here is used as your display name (min 3 chars).</p>
          <input type="text" v-model="editableName"
            class="w-full border border-gray-300 text-gray-900 text-sm rounded-md block p-2.5 focus:ring-1 focus:ring-black focus:border-black tracking-wide"
            placeholder="Your Name">
        </div>

        <div v-if="saveMessage" class="text-green-600 text-sm font-medium">{{ saveMessage }}</div>
        <div v-if="saveError" class="text-red-600 text-sm font-medium">{{ saveError }}</div>
      </div>

      <div class="px-6 py-4 bg-gray-50 text-right rounded-b-xl border-t border-gray-100">
        <button @click="handleSave" :disabled="!isNameValid || isSaving"
          class="bg-gray-900 text-white px-6 py-2 rounded-md text-sm font-bold hover:bg-gray-800 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full sm:w-auto sm:ml-auto">
          <svg v-if="isSaving" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          {{ isSaving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>

    <div class="bg-white rounded-xl border border-gray-100 shadow-sm">
      <div class="px-6 py-5 border-b border-gray-100">
        <h2 class="text-lg text-red-600">Danger zone</h2>
      </div>
      <div class="p-6">
        <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <input id="delete-checkbox" type="checkbox" v-model="isDeleteAcknowledged"
            class="mt-0.5 w-4 h-4 text-red-600 bg-white border-gray-300 rounded focus:ring-red-500 focus:ring-offset-0 cursor-pointer">
          <label for="delete-checkbox" class="ml-2 text-sm font-medium text-gray-900 cursor-pointer">
            I acknowledge that deleting my user account permanently removes my access to all organizations and anything
            under organizations. This action cannot be undone.
          </label>
        </div>
      </div>

      <div class="px-6 py-4 bg-gray-50 text-right rounded-b-xl border-t border-gray-100">
        <button @click="handleDelete" :disabled="!isDeleteAcknowledged || isDeleting" :class="[
          !isDeleteAcknowledged || isDeleting
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-red-600 hover:bg-red-700 cursor-pointer'
        ]" class="text-white px-6 py-2 rounded-md text-sm font-bold transition flex items-center gap-2 ml-auto">
          <svg v-if="isDeleting" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          {{ isDeleting ? 'Deleting...' : 'Delete account' }}
        </button>
      </div>
    </div>
  </div>
</template>